/**
 * ActivitiesMiniCalendar - Mini calendário de agendamentos
 * Exibe indicadores nos dias com atividades agendadas
 * Ao clicar em um dia, filtra a timeline
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 */
import React, { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { startOfMonth, endOfMonth, format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

// Props do componente
interface ActivitiesMiniCalendarProps {
  onSelectDate: (date: Date | undefined) => void;
  selectedDate?: Date;
}

/**
 * Mini calendário de agendamentos de atividades
 */
export function ActivitiesMiniCalendar({ onSelectDate, selectedDate }: ActivitiesMiniCalendarProps) {
  const { currentTenant } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Query para buscar dias com agendamentos no mês
  const { data: scheduledDays = [] } = useQuery({
    queryKey: ['activities-scheduled-days', currentTenant?.id, format(currentMonth, 'yyyy-MM')],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);

      // v1.1.0: usa RPC server-side `get_activities_scheduled_days` para evitar
      // SELECT direto em crm_deal_activities (padrão de abstração server-side)
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data, error } = await supabase.rpc('get_activities_scheduled_days', {
        p_month_start: format(monthStart, 'yyyy-MM-dd'),
        p_month_end: format(monthEnd, 'yyyy-MM-dd'),
        p_timezone: tz,
        p_tenant_hint: currentTenant.id,
      });

      if (error) return [];

      // Converte cada `scheduled_date` (string YYYY-MM-DD) em Date local
      return (data || []).map((row: { scheduled_date: string }) => {
        const [y, m, d] = row.scheduled_date.split('-').map(Number);
        return new Date(y, m - 1, d);
      });
    },
    enabled: !!currentTenant?.id,
    staleTime: 60 * 1000, // Cache de 1 minuto
  });

  // Modificadores para dias com agendamentos
  const modifiers = {
    scheduled: scheduledDays,
  };

  // Classes para os modificadores
  const modifiersClassNames = {
    scheduled: 'relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-orange-500',
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="py-3 px-4 border-b">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          Agendamentos
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={onSelectDate}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={ptBR}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          className="p-0"
        />
        
        {/* Legenda */}
        <div className="flex items-center justify-center gap-4 mt-2 pt-2 border-t text-[10px] text-muted-foreground">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            <span>Agendamento pendente</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
